import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { STATIC_BASE_URL } from '../api';

// Utility: number to words (as before)
const numberToWords = (num) => {
  // ... (paste your numberToWords logic here)
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  if ((num = num.toString()).length > 9) return 'Amount too large';
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] !== '00') ? (a[Number(n[1])] || (b[n[1][0]] + ' ' + a[n[1][1]])) + ' Crore ' : '';
  str += (n[2] !== '00') ? (a[Number(n[2])] || (b[n[2][0]] + ' ' + a[n[2][1]])) + ' Lakh ' : '';
  str += (n[3] !== '00') ? (a[Number(n[3])] || (b[n[3][0]] + ' ' + a[n[3][1]])) + ' Thousand ' : '';
  str += (n[4] !== '0')  ? (a[Number(n[4])] || (b[n[4][0]] + ' ' + a[n[4][1]])) + ' Hundred ' : '';
  str += (n[5] !== '00') ? ((str ? 'and ' : '') + (a[Number(n[5])] || (b[n[5][0]] + ' ' + a[n[5][1]]))) + ' ' : '';
  return str + 'Rupees';
};

const formatDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
};

const styles = StyleSheet.create({
  page: { padding: 10, fontSize: 9, backgroundColor: '#fff' },
  row: { flexDirection: 'row' },
  col: { flex: 1 },
  border: { border: '1 solid #000' },
  title: { fontWeight: 'bold', fontSize: 13, marginBottom: 2, textAlign: 'center', textDecoration: 'underline' },
  logo: { width: 45, height: 45, marginRight: 8 },
  bold: { fontWeight: 'bold' },
  mb2: { marginBottom: 2 },
  mb4: { marginBottom: 4 },
  table: { display: 'table', width: 'auto', marginTop: 3, marginBottom: 4 },
  tableRow: { flexDirection: 'row' },
  tableHeadCell: { border: '1 solid #000', fontWeight: 'bold', padding: 3, fontSize: 9, textAlign: 'center', backgroundColor: '#fff' },
  tableCell: { border: '1 solid #000', padding: 3, fontSize: 9, textAlign: 'center' },
  tableCellLeft: { border: '1 solid #000', padding: 3, fontSize: 9, textAlign: 'left' },
  small: { fontSize: 7 },
  rightText: { textAlign: 'right', fontSize: 9 },
  grandTotal: { fontSize: 11, fontWeight: 'bold', textAlign: 'right' },
});

const tableColumnWidths = {
  no: '10%',
  desc: '32%',
  size: '16%',
  qty: '10%',
  rate: '16%',
  total: '16%'
};

const taxColumnWidths = {
  taxable: '20%',
  cgstPercent: '12%',
  cgstAmt: '16%',
  sgstPercent: '12%',
  sgstAmt: '16%',
  totalTax: '24%'
};

const ITEMS_PER_PAGE = 10; // for A5, adjust as needed after testing

export default function InvoiceDocument({ invoice, items, settings, logo, customer, qrCodeDataUrl }) {
  if (!invoice || !customer || !settings) return null;
  const phoneNumbers = (settings.Phone || '')
    .split(',')
    .map(num => num.trim())
    .filter(Boolean);

  // Calculations
  const subTotal = items.reduce((sum, i) => sum + Number(i.Quantity) * Number(i.Rate), 0);
  const discount = Number(invoice.Discount || 0);
  const netTotal = subTotal - discount;
  const gstPercent = items.length > 0 ? Number(items[0].GSTPercent) : 18;
  const halfGst = gstPercent / 2;
  const cgstAmount = (subTotal * halfGst) / 100;
  const sgstAmount = (subTotal * halfGst) / 100;
  const totalTax = cgstAmount + sgstAmount;
  const dueDate = new Date(invoice.InvoiceDate);
  dueDate.setDate(dueDate.getDate() + 30);

  // Split items for multipage
  const pages = [];
  for (let i = 0; i < items.length; i += ITEMS_PER_PAGE) {
    pages.push(items.slice(i, i + ITEMS_PER_PAGE));
  }

  return (
    
    <Document>
      {pages.map((itemPage, pageIndex) => (
        <Page size="A5" style={styles.page} key={pageIndex}>
          {/* HEADER (repeat on every page) */}
          <View
  style={{
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
    justifyContent: 'space-between',
    width: '100%'
  }}
>
  {/* Left: Logo + business info */}
  <View style={{ flexDirection: 'row', flex: 1, alignItems: 'flex-start' }}>
    {logo && logo.FileName && (
      <Image
        src={`${STATIC_BASE_URL}/uploads/logos/${logo.FileName}`}
        style={styles.logo}
      />
    )}
    <View>
      <Text style={{ fontWeight: 'bold', fontSize: 12 }}>{settings.BusinessName}</Text>
      <Text style={styles.small}>{settings.Address}</Text>
      <View style={{ flexDirection: 'row', marginBottom: 1 }}>
        <Text style={styles.small}>GSTIN: {settings.GSTIN}</Text>
        {!!settings.PANNumber && (
          <Text style={[styles.small, { marginLeft: 10 }]}>| PAN: {settings.PANNumber}</Text>
        )}
      </View>
    </View>
  </View>
  {/* Right: Phones in 3 lines, right-aligned, icon only first line */}
  <View style={{ flexDirection: 'column', alignItems: 'flex-end', minWidth: 70 }}>
  {phoneNumbers.map((num, idx) => (
    <View key={idx} style={{ flexDirection: 'row', alignItems: 'center', minHeight: 13 }}>
      <Text style={{ marginRight: 2, fontSize: 9, width: 18, textAlign: 'center' }}>
  {idx === 0 ? 'Ph:' : ''}
</Text>
      <Text style={styles.small}>{num}</Text>
    </View>
  ))}
</View>

</View>

          <Text style={styles.title}>GST TAX INVOICE</Text>
          {/* BILL TO / SHIP TO / DETAILS */}
          <View style={[styles.row, { marginBottom: 3 }]}>
            <View style={[styles.col, styles.border, { padding: 3 }]}>
              <Text style={styles.bold}>Bill To</Text>
              <Text>{customer.name}</Text>
              <Text>{customer.address}</Text>
              <Text>Mobile: {customer.phone}</Text>
              <Text>GSTIN: {customer.gstin}</Text>
            </View>
            <View style={[styles.col, styles.border, { padding: 3 }]}>
              <Text style={styles.bold}>Ship To</Text>
              <Text>{customer.name}</Text>
              <Text>{customer.address}</Text>
              <Text>Mobile: {customer.phone}</Text>
              <Text>GSTIN: {customer.gstin}</Text>
            </View>
            <View style={[styles.col, styles.border, { padding: 3 }]}>
              <Text>Invoice No: {invoice.InvoiceNo}</Text>
              <Text>Invoice Date: {formatDate(invoice.InvoiceDate)}</Text>
              <Text>Due Date: {formatDate(dueDate)}</Text>
            </View>
          </View>
          {/* ITEMS TABLE */}
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeadCell, {width: tableColumnWidths.no}]}>No.</Text>
              <Text style={[styles.tableHeadCell, {width: tableColumnWidths.desc}]}>Description</Text>
              <Text style={[styles.tableHeadCell, {width: tableColumnWidths.size}]}>Size</Text>
              <Text style={[styles.tableHeadCell, {width: tableColumnWidths.qty}]}>Qty</Text>
              <Text style={[styles.tableHeadCell, {width: tableColumnWidths.rate}]}>Rate</Text>
              <Text style={[styles.tableHeadCell, {width: tableColumnWidths.total}]}>Total</Text>
            </View>
            {itemPage.map((item, idx) => (
              <View style={styles.tableRow} key={idx}>
                <Text style={[styles.tableCell, {width: tableColumnWidths.no}]}>{pageIndex * ITEMS_PER_PAGE + idx + 1}</Text>
                <Text style={[styles.tableCellLeft, {width: tableColumnWidths.desc}]}>{item.Description}</Text>
                <Text style={[styles.tableCell, {width: tableColumnWidths.size}]}>{item.Size}</Text>
                <Text style={[styles.tableCell, {width: tableColumnWidths.qty}]}>{item.Quantity}</Text>
                <Text style={[styles.tableCell, {width: tableColumnWidths.rate}]}>{parseFloat(item.Rate).toFixed(2)}</Text>
                <Text style={[styles.tableCell, {width: tableColumnWidths.total}]}>{(item.Quantity * item.Rate).toFixed(2)}</Text>
              </View>
            ))}
          </View>
          {/* Only show totals, tax, QR, bank etc. on last page */}
          {pageIndex === pages.length - 1 && (
            <>
              {/* TAX SUMMARY TABLE */}
              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableHeadCell, {width: taxColumnWidths.taxable}]}>Taxable Value</Text>
                  <Text style={[styles.tableHeadCell, {width: taxColumnWidths.cgstPercent}]}>CGST %</Text>
                  <Text style={[styles.tableHeadCell, {width: taxColumnWidths.cgstAmt}]}>CGST Amt</Text>
                  <Text style={[styles.tableHeadCell, {width: taxColumnWidths.sgstPercent}]}>SGST %</Text>
                  <Text style={[styles.tableHeadCell, {width: taxColumnWidths.sgstAmt}]}>SGST Amt</Text>
                  <Text style={[styles.tableHeadCell, {width: taxColumnWidths.totalTax}]}>Total Tax</Text>
                </View>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, {width: taxColumnWidths.taxable}]}>{subTotal.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, {width: taxColumnWidths.cgstPercent}]}>{halfGst}</Text>
                  <Text style={[styles.tableCell, {width: taxColumnWidths.cgstAmt}]}>{cgstAmount.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, {width: taxColumnWidths.sgstPercent}]}>{halfGst}</Text>
                  <Text style={[styles.tableCell, {width: taxColumnWidths.sgstAmt}]}>{sgstAmount.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, {width: taxColumnWidths.totalTax}]}>{totalTax.toFixed(2)}</Text>
                </View>
              </View>
              {/* DISCOUNT + GRAND TOTAL */}
              <View style={[styles.row, { justifyContent: 'flex-end', marginBottom: 1 }]}>
                <Text style={styles.rightText}>Discount: {discount.toFixed(2)} </Text>
                <Text style={styles.grandTotal}>Grand Total: {netTotal.toFixed(2)}</Text>
              </View>
              {/* AMOUNT IN WORDS */}
              <View style={{ border: '1 solid #000', padding: 3, marginBottom: 2 }}>
                <Text style={{ fontSize: 8 }}>
                  <Text style={{ fontWeight: 'bold' }}>Total Amount (in words): </Text>
                  {numberToWords(netTotal.toFixed(0))}
                </Text>
              </View>
              {/* BANK + QR + TERMS + SIGNATORY */}
              <View style={[styles.row, styles.border, { marginBottom: 2 }]}>
                {/* Bank */}
                <View style={{ flex: 1, borderRight: '1 solid #000', padding: 4 }}>
                  <Text style={styles.bold}>Bank Details</Text>
                  <Text>{settings.BankName}</Text>
                  <Text>IFSC: {settings.IFSCCode}</Text>
                  <Text>Ac/No: {settings.AccountNumber}</Text>
                  <Text>Branch: {settings.BankBranch}</Text>
                </View>
                {/* QR */}
                <View style={{ flex: 1, borderRight: '1 solid #000', padding: 4, alignItems: 'center' }}>
                  <Text style={styles.bold}>Payment QR</Text>
                  {qrCodeDataUrl && (
                    <Image style={{ width: 64, height: 64, margin: 'auto' }} src={qrCodeDataUrl} />
                  )}
                  <Text style={styles.small}>UPI: h.patne5577@axl</Text>
                </View>
                {/* Terms */}
                <View style={{ flex: 1, borderRight: '1 solid #000', padding: 4 }}>
                  <Text style={styles.bold}>Terms & Conditions</Text>
                  <Text style={styles.small}>1. Goods once sold will not be taken back or exchanged.</Text>
                  <Text style={styles.small}>2. All disputes subject to jurisdiction only.</Text>
                </View>
                {/* Signatory */}
                <View style={{ flex: 1, padding: 4 }}>
                  <Text style={styles.bold}>Authorized Signatory</Text>
                  <Text style={{ borderTop: '1 solid #000', marginTop: 20 }}></Text>
                  <Text>For {settings.BusinessName}</Text>
                </View>
              </View>
              {/* FOOTER */}
              <Text style={{ textAlign: 'center', fontSize: 8, marginTop: 3 }}>Thank you, visit again!</Text>
            </>
          )}
        </Page>
      ))}
    </Document>
  );
}
